// The full review: the "What to do" card when something needs you, the progress tracker,
// then the PDF beside the checks (what differs first), the audit log and the technical details.
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInvoice } from "../api/hooks";
import { checksOf } from "../shared/checks";
import { BackLink, ErrorCard, LoadingRows, OutcomePill, ProgressCompact, mono } from "../shared/components";
import { invoiceRef, outcomeOf, stepIndex, poRef } from "../shared/format";
import { AuditList, ChecksList, CompareTables, PdfView, ProgressTracker, TechDetails, JobLink } from "../shared/invoiceParts";
import { Card, Tabs } from "../ui";
import { Page } from "../shell/Shell";
import { NeedsYou, useInvoiceActions } from "./ProblemDetail";


export function InvoiceReview() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const detail = useInvoice(id);
  const actions = useInvoiceActions();
  const [tab, setTab] = useState("checks");
  const d = detail.data;
  const checks = useMemo(() => (d ? checksOf(d) : []), [d]);

  if (detail.isPending) return <Page><LoadingRows rows={6} /></Page>;
  if (detail.error || !d) return <Page><ErrorCard error={detail.error ?? new Error("Not found")} retry={() => void detail.refetch()} /></Page>;
  const inv = d.invoice;
  const o = outcomeOf(inv);
  const working = actions.working(inv);
  const active = o === "rev" || o === "stop" || o === "set" || working;
  const po = poRef(inv);

  return (
    <Page>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <BackLink onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/queue"))} label="Invoice Queue" />
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          {/* The PO leads: it is what the page is about in Syncore; the supplier and invoice number follow. Without a PO the supplier leads. */}
          <h1 style={{ font: "800 28px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 }}>{po ? `PO ${po}` : inv.supplier ?? invoiceRef(inv)}</h1>
          <span style={{ ...mono, fontSize: 17, color: "var(--text-muted)" }}>{po ? [inv.supplier ?? invoiceRef(inv), inv.invoiceNumber].filter(Boolean).join(" – ") : inv.invoiceNumber ?? ""}</span>
          <OutcomePill o={working ? "prog" : o} below readyLine={actions.readyLine} />
          <span style={{ flex: 1 }} />
          <JobLink jobNumber={inv.jobNumber} size={15} />
        </div>
        {active && <div style={{ padding: "18px 20px", borderRadius: "var(--radius-xl)", background: "var(--surface-card)", boxShadow: "0 0 0 1.5px var(--av-blue-200), var(--shadow-sm)" }}><NeedsYou detail={d} actions={actions} /></div>}
        <Card padding="lg">{(o === "prog" || o === "wait") && !active ? <ProgressCompact step={stepIndex(inv.step)} label={inv.step ?? (o === "wait" ? "Queued" : null)} big /> : <ProgressTracker detail={d} checks={checks} readyLine={actions.readyLine} />}</Card>
        <div className="avp-rev">
          <div className="avp-rev-pdf"><PdfView invoiceId={inv.id} fileName={inv.fileName} available={inv.pdfAvailable} /></div>
          <Card padding="lg" style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
            <Tabs variant="underline" value={tab} onChange={setTab} items={[{ value: "checks", label: "Checks" }, { value: "audit", label: "Audit log", count: d.audit.length }, { value: "tech", label: "Technical details" }]} />
            {tab === "checks" && <><div style={{ font: "800 15px var(--font-display)", color: "var(--text-strong)" }}>All {checks.length} checks</div><ChecksList checks={checks} cols={2} /><div style={{ height: 1, background: "var(--border-subtle)" }} /><div style={{ font: "800 17px var(--font-display)", color: "var(--text-strong)" }}>PO vs invoice</div>{d.invoiceLines ? <CompareTables detail={d} currency={actions.currency} /> : <span style={{ color: "var(--text-muted)" }}>Not read yet.</span>}</>}
            {tab === "audit" && <AuditList entries={d.audit} />}
            {tab === "tech" && <TechDetails detail={d} />}
          </Card>
        </div>
      </div>
      {actions.dialogs}
    </Page>
  );
}
